// A gentle, one-time "keep this on your home screen" tip (.wiki/inventory/candidates/exp-bookmark-install-prompt.md).
// Shown below the game, never over it, only after the third day this browser has visited, never when the site is
// already running from the home screen, and never again once answered. The text depends on what the browser can do:
// Chrome/Edge/Android can install with one button; iPhone and iPad need Share → Add to Home Screen; other desktops
// get a bookmark tip.
import { deviceClass, track } from "./analytics"

interface InstallPrompt extends Event { prompt(): Promise<void>; userChoice: Promise<{ outcome: "accepted" | "dismissed" }> }

const STATE = "dt:install-tip"
const DAYS = "dt:visit-days"
const MIN_DAYS = 3
// iPhone and iPad from the second day: Safari deletes a site's saved data after 7 days of use without a visit, and
// home-screen apps are exempt (.wiki/wiki/topics/retention-no-account-persistence.md), so the tip matters most there.
const MIN_DAYS_IOS = 2
let deferred: InstallPrompt | null = null
let shown = false

const get = (k: string): string | null => { try { return localStorage.getItem(k) } catch { return null } }
const set = (k: string, v: string): void => { try { localStorage.setItem(k, v) } catch { /* blocked: the tip just shows again */ } }

export const standalone = (): boolean =>
  matchMedia("(display-mode: standalone)").matches || (navigator as Navigator & { standalone?: boolean }).standalone === true

const isIOS = (): boolean => /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.userAgent.includes("Macintosh") && "ontouchend" in document)

/** Counts distinct days this browser has visited; called once per visit with yesterday-or-earlier's date. */
export function countVisitDay(lastVisit: string | null, today: string): void {
  if (lastVisit === today) return
  set(DAYS, String(Number(get(DAYS) ?? 0) + 1))
}

function show(kind: "prompt" | "ios" | "bookmark"): void {
  const slot = document.querySelector<HTMLElement>("[data-install-tip]")
  if (!slot || shown) return
  shown = true
  const desktop = deviceClass(window.innerWidth, matchMedia("(pointer: coarse)").matches) === "desktop"
  const heading = document.createElement("h2")
  const body = document.createElement("p")
  const buttons = document.createElement("p")
  buttons.className = "options"
  const button = (label: string, onClick: () => void): void => {
    const b = document.createElement("button")
    b.type = "button"; b.className = "btn"; b.textContent = label
    b.addEventListener("click", onClick)
    buttons.appendChild(b)
  }
  const close = (answer: string): void => { set(STATE, answer); slot.hidden = true; track("install_tip", { kind: answer, value: kind }) }
  if (kind === "prompt") {
    heading.textContent = desktop ? "Keep these games one click away" : "Keep these games on your home screen"
    body.textContent = desktop
      ? "Install Card & Puzzle Games on this computer. It opens in its own window and works without internet."
      : "Add Card & Puzzle Games to your home screen. It opens like an app and works without internet."
    button(desktop ? "Install" : "Add to home screen", () => {
      const p = deferred
      deferred = null
      if (!p) return close("dismissed")
      void p.prompt().then(() => p.userChoice).then((c) => close(c.outcome === "accepted" ? "accepted" : "dismissed"), () => close("dismissed"))
    })
  } else if (kind === "ios") {
    heading.textContent = "Keep these games on your home screen"
    body.textContent = "Tap the Share button (a square with an arrow pointing up), then choose Add to Home Screen. The games then open like an app and work without internet, and your saved games and calendar are kept: Safari can clear them if you don't visit for a week."
  } else {
    heading.textContent = "Bookmark these games"
    body.textContent = /Mac/.test(navigator.userAgent) ? "Press ⌘D to bookmark this page." : "Press Ctrl+D to bookmark this page."
  }
  button(kind === "prompt" ? "No thanks" : "Got it", () => close("dismissed"))
  slot.replaceChildren(heading, body, buttons)
  slot.hidden = false
  track("install_tip", { kind: "shown", value: kind })
}

function maybeShow(): void {
  if (shown || standalone() || get(STATE) || Number(get(DAYS) ?? 0) < (isIOS() ? MIN_DAYS_IOS : MIN_DAYS)) return
  if (!document.querySelector("[data-install-tip]")) return
  if (deferred) return show("prompt")
  if (isIOS()) return show("ios")
  // Chromium may still offer an install prompt a moment later; only a non-Chromium desktop gets the bookmark tip.
  if (deviceClass(window.innerWidth, matchMedia("(pointer: coarse)").matches) === "desktop" && !/Chrome\/|Edg\//.test(navigator.userAgent)) show("bookmark")
}

export function initInstallTip(): void {
  addEventListener("beforeinstallprompt", (e) => { e.preventDefault(); deferred = e as InstallPrompt; maybeShow() })
  addEventListener("appinstalled", () => { set(STATE, "installed"); track("install_tip", { kind: "installed" }) })
  maybeShow()
}
